import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';

/**
 * Google OAuth callback page.
 *
 * We deliberately do NOT use supabase.auth here. The Supabase JS client
 * insists on calling /auth/v1/user with the anon key right after parsing
 * the hash, and that call has been returning 401 in this project (mixed
 * HS256 / ES256 signing config). We only need the raw access_token to
 * hand to our backend, which does its own verification.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const hashParams = new URLSearchParams(
      (window.location.hash || '').replace(/^#/, '')
    );
    const searchParams = new URLSearchParams(window.location.search);

    const urlError =
      hashParams.get('error_description') ||
      hashParams.get('error') ||
      searchParams.get('error_description') ||
      searchParams.get('error');

    if (urlError) {
      setError(decodeURIComponent(urlError));
      return undefined;
    }

    const accessToken = hashParams.get('access_token');
    if (!accessToken) {
      setError(
        'No access token in callback URL. Did you land here without completing Google sign-in?'
      );
      return undefined;
    }

    const exchangeWithBackend = async () => {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/auth/google`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_token: accessToken }),
        }
      );

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || `Backend rejected token (${response.status})`);
      }

      const result = await response.json();
      if (cancelled) return;
      login(result.user.email, result.access_token);
      // Scrub the fragment so the access_token isn't left in browser history.
      window.history.replaceState(null, '', window.location.pathname);
      navigate('/', { replace: true });
    };

    exchangeWithBackend().catch((e) => {
      if (!cancelled) setError(e.message || 'Google sign-in failed.');
    });

    return () => {
      cancelled = true;
    };
  }, [login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white">
      <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 max-w-md w-full mx-4">
        {error ? (
          <>
            <h2 className="text-xl font-semibold mb-3">Sign-in failed</h2>
            <p className="text-red-300 text-sm mb-4">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full py-3 px-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl"
            >
              Back to login
            </button>
          </>
        ) : (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3" />
            <span className="text-gray-200">Finishing sign-in…</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
